import React, { useEffect, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { useTheme, styled } from '@mui/styles'
import { Box, Typography, Grid, Button as MButton } from '@mui/material'
import Button from 'core/buttons/Button'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useWineContext } from 'providers/WinesProvider'
import useWineriesApi from 'firebaseKit/wineries'
import useWinesApi from 'firebaseKit/wines'
import { usePointsContext } from 'providers/PointsProvider'
/* eslint import/no-webpack-loader-syntax: off */
import EmailInput from './EmailInput'
import { useGameMode } from 'providers/GameMode'
import useMailApi from 'firebaseKit/mail'
import useVenuesApi from 'firebaseKit/venues'
import EmailTemplate from './EmailTemplate'
import { useVenueContext } from 'providers/VenueProvider'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons'

const MuiButton = styled(MButton)(() => ({
  position: 'absolute',
  width: '50px',
  bottom: -1,
  right: -1,
  background: '#2E2A26',
  borderRadius: 0,
  borderTopLeftRadius: '15px',
  borderBottomRightRadius: '15px',
  '&:hover': {
    background: '#2E2A26',
    opacity: 0.95
  }
}))

const InvestorDeck = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { guessed, setGuessed, getStoragePlayerData, player, setGameData, setPlayer } = useGameMode()
  const [emailSent, setEmailSent] = useState(false)
  const [isMultiplayer] = useState(pathname.includes('game-room'))
  const points = Math.round(isMultiplayer ? player?.totalPoints ?? guessed?.points : guessed?.points)
  const { id } = useParams()
  const { wines, setWineList } = useWineContext()
  const { getWinery } = useWineriesApi()
  const { getWinesByWynery } = useWinesApi()
  const { fullyRatedWineries, getWineNotes, getWineRating } = usePointsContext()
  const [wineryData, setWineryData] = useState(null)

  const [loading, setLoading] = useState(false)

  const { sendMail } = useMailApi()
  const { submitEmailForGameSession } = useVenuesApi()
  const { currentVenueData, isDefaultVenue } = useVenueContext()

  useEffect(() => {
    getStoragePlayerData().then(storageData => {
      if (isMultiplayer) {
        if (!storageData) navigate('/game-mode')
        if (!guessed?.wineryName && storageData && !('totalPoints' in storageData)) {
          navigate(pathname.replace('investor-deck', 'guess-price'))
        }
      } else { if (!guessed?.wineryName) navigate(pathname.replace('investor-deck', 'guess-price')) }
    })
    getWinesByWynery(id).then((wineList) => {
      setWineList(wineList)
    })
    window.scrollTo({ top: 0 })
  }, [])

  useEffect(() => {
    getWinery(id).then((data) => setWineryData(data))
  }, [id])

  const pointsLength = points.toString().length
  const pointsWithDecimals = points && points.toString().includes('.')

  // Send rating stars for email template
  const getStarsImage = (rate) => {
    switch (rate) {
      case 1:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/one_star.png?alt=media&token=f8ead3d1-9603-4d63-816a-365213cb09bf'
      case 1.5:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/one_half_star.png?alt=media&token=03877ef3-9ecd-4683-a80c-54afe05021bf'
      case 2:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/two_star.png?alt=media&token=f3f4a4a2-e830-43b3-9d8f-a671effbeff7'
      case 2.5:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/two_half_star.png?alt=media&token=bc7fb4ef-750b-4824-bfe6-43b6baf0db30'
      case 3:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/three_star.png?alt=media&token=2139dd28-c9c8-45cb-845f-592ab311b1dd'
      case 3.5:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/three_half_star.png?alt=media&token=05c56d16-06ea-4c59-831c-35829db6c462'
      case 4:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/four_star.png?alt=media&token=87c6ea59-7b15-448d-b6f0-428b449c629f'
      case 4.5:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/four_half_star.png?alt=media&token=fb8ddbd7-3675-47a1-b716-7e8c69ccc07a'
      case 5:
        return 'https://firebasestorage.googleapis.com/v0/b/wine-smash-dev.appspot.com/o/five_star.png?alt=media&token=1e49ad9a-348f-4b2f-a3e9-f24f7958020a'
      default:
        return ''
    }
  }

  const emailTemplateProps = {
    app_url: process.env.REACT_APP_URL,
    items: wines && Array.isArray(wines)
      ? wines.map((wine) => ({
        producer: wine.producer || wine.winery,
        name: wine.name,
        grape: `${wine.grape} - ${wine.year}`,
        image: wine.image,
        color: wine.backgroundColor,
        rating: getStarsImage(getWineRating(wine.name)),
        tasting_notes: getWineNotes(wine.name),
        url: wine.url
      }))
      : [],
    score: points,
    to_email: null
  }

  const sendEmail = (values) => {
    emailTemplateProps.to_email = values.email

    setLoading(true)

    const htmlDiv = renderToString(
      <EmailTemplate
        props={emailTemplateProps}
        isDefaultVenue={isDefaultVenue}
        venue={currentVenueData}
      />
    )
    const contentHtml = `<!DOCTYPE html>\n<html>\n<head>\n</head>\n<body style="font-family: Roboto, Helvetica, Arial, sans-serif">${htmlDiv}</body></html>`

    // Generate e-mail text content
    const subject = `I just scored ${emailTemplateProps.score} points at Wine Smash!`
    let contentText = `${subject}\n\nToday's wines:\n\n`
    contentText += emailTemplateProps.items.map((item) => {
      let itemLine = ' - ' + (
        [
          item.producer,
          item.name,
          item.grape
        ]
          .filter((v) => !!v && v.length > 0)
          .join(' ')
      )
      if (item.url) {
        itemLine += `\n  - ${item.url}`
      }
      if (item.tasting_notes) {
        itemLine += `\n  - ${item.tasting_notes}`
      }
      return itemLine
    })
      .join('\n')

    // Submit e-mail and marketing flag to game session
    const currentSessionId = localStorage.getItem('current_game_session')
    if (currentSessionId) {
      try {
        submitEmailForGameSession(currentSessionId, values.email, values['receive-marketing'])
      } catch (err) {
        console.error(err)
      }
    }

    sendMail(
      values.email, // TO
      subject,
      contentText,
      contentHtml,
      [], // CC
      ['winelist@winesmash.com'], // BCC
      { marketing: values['receive-marketing'] }
    )
      .then(
        (result) => {
          console.log(result.text)
          setLoading(false)
          setEmailSent(true)
        },
        (error) => {
          console.log(error.text)
          setLoading(false)
        }
      )
  }

  const endCurrentPlayerGame = () => {
    localStorage.removeItem('multiplayer')
    localStorage.removeItem('rating')
    localStorage.removeItem('scoring')
    localStorage.removeItem('tasting_notes')
    setGameData(null)
    setPlayer({ isHost: null, nickname: null, isReady: null, color: null })
    navigate('/game-mode')
  }
  const playAgain = () => {
    setGuessed({ ...guessed, guessed: false })
    if (isMultiplayer) {
      endCurrentPlayerGame()
    } else {
      fullyRatedWineries()
      navigate('/home')
    }
  }

  const getThankyouMessage = () => {
    const playerName = localStorage.getItem('current_player_name')
    return playerName && playerName.toString().trim().length > 0
      ? `Thank you ${playerName} for playing Wine Smash!`
      : 'Thank you for playing Wine Smash!'
  }
  return (
    <Box style={theme.background} sx={{ position: 'relative', display: 'flex', flexDirection: 'column', m: 'auto', overflow: 'hidden' }}>
      <Box component="img" src="/background.svg" alt=""
      sx={{ position: 'absolute', top: -100, width: '100%', height: '100%', transform: 'scale(1.25)' }} />

      <Box sx={{ zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography fontFamily="KaiseiDecolBold" fontSize={32} sx={{ mt: 8, mb: 4, textAlign: 'center' }}>
          {getThankyouMessage()}
        </Typography>
        <Typography fontFamily="MundialRegular" fontSize={16}>
          Your final score
        </Typography>
      </Box>

      <Box sx={{ zIndex: 10, px: 4, background: 'linear-gradient(to bottom, rgba(255,253,247,0), rgb(250,243,237))' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ position: 'relative', mt: 4 }}>
            <Typography
              fontFamily="KaiseiDecolBold"
              fontSize={pointsLength >= 4 && pointsWithDecimals ? 50 : pointsLength === 3 ? 54 : 66}
              sx={{
                color: 'white',
                position: 'absolute',
                [theme.breakpoints.up('sm')]: {
                  left:
                    pointsLength === 5 && pointsWithDecimals
                      ? 0
                      : pointsLength === 4 && pointsWithDecimals
                        ? 5
                        : pointsLength === 3 && pointsWithDecimals
                          ? 18
                          : pointsLength === 2 && pointsWithDecimals
                            ? 27
                            : pointsLength === 1 && pointsWithDecimals
                              ? 27
                              : pointsLength === 3
                                ? 10
                                : pointsLength === 2
                                  ? 20
                                  : 40,
                  top:
                    pointsLength === 5 && pointsWithDecimals
                      ? 20
                      : pointsLength === 4 && pointsWithDecimals
                        ? 20
                        : pointsLength === 3 && pointsWithDecimals
                          ? 15
                          : pointsLength === 2 && pointsWithDecimals
                            ? 27
                            : pointsLength === 1 && pointsWithDecimals
                              ? 27
                              : pointsLength === 3
                                ? 15
                                : pointsLength === 2
                                  ? 4
                                  : 4
                },
                left:
                  pointsLength === 5 && pointsWithDecimals
                    ? 0
                    : pointsLength === 4 && pointsWithDecimals
                      ? 5
                      : pointsLength === 3 && pointsWithDecimals
                        ? 18
                        : pointsLength === 2 && pointsWithDecimals
                          ? 27
                          : pointsLength === 1 && pointsWithDecimals
                            ? 27
                            : pointsLength === 3
                              ? 10
                              : pointsLength === 2
                                ? 20
                                : 40,
                top:
                  pointsLength === 5 && pointsWithDecimals
                    ? 20
                    : pointsLength === 4 && pointsWithDecimals
                      ? 20
                      : pointsLength === 3 && pointsWithDecimals
                        ? 15
                        : pointsLength === 2 && pointsWithDecimals
                          ? 27
                          : pointsLength === 1 && pointsWithDecimals
                            ? 27
                            : pointsLength === 3
                              ? 15
                              : pointsLength === 2
                                ? 4
                                : 4
              }}
            >
              {points}
            </Typography>

            <Typography fontFamily="MundialBold" fontSize={18} sx={{ color: 'white', position: 'absolute', bottom: 30, left: 32 }}>
              Points
            </Typography>

            <Box component="img" src="/emptyStar.svg" alt="star" sx={{ width: 120 }} />
          </Box>

          <Box sx={{ width: 1, mt: 2, mb: 1 }}>
            <Box sx={{ my: 2, border: '2px solid #2E2A26', borderRadius: '20px', minHeight: 110, position: 'relative' }}>
              <Grid container>
                <Grid item xs={3} sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: 27, top: 30 }}>
                    {Array.isArray(wines) && wines.slice(3).map((wine, idx) =>
                      <Box
                        key={idx}
                        component="img"
                        src={wine.image}
                        alt="bootles"
                        sx={{
                          width: idx === 0 ? 40 : 30,
                          position: 'absolute',
                          zIndex: idx === 0 ? 20 : 10,
                          left: idx === 1 ? 18 : (idx === 2) ? -15 : 'auto',
                          top: idx === 0 ? 'auto' : 7
                        }}
                      />)}
                  </Box>
                </Grid>

                {isDefaultVenue &&
                  <Grid item xs={9}>
                    <Grid container sx={{ [theme.breakpoints.up('sm')]: { p: 0 }, pl: 2 }}>
                      <Grid item xs={12}>
                        <Typography fontFamily="KaiseiDecolBold" fontSize={18} sx={{ my: 1 }}>
                          Enjoyed this game?
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontFamily="DMSansRegular" fontSize={16} sx={{ pb: 2.5, pr: 0.5 }}>
                          <p>If you would like to buy any of the wines tasted in this game, please click this link to order.</p>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>}

                {!isDefaultVenue &&
                  <Grid item xs={9}>
                    <Grid container sx={{ [theme.breakpoints.up('sm')]: { p: 0 }, pl: 2 }}>
                      <Grid item xs={12}>
                        <Typography fontFamily="KaiseiDecolBold" fontSize={18} sx={{ my: 1 }}>
                          Enjoyed this game?
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontFamily="DMSansRegular" fontSize={16} sx={{ pr: 0.5 }}>
                          <p style={{ margin: 0 }}>Why not have another glass of your favourite now!</p>
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontFamily="DMSansRegular" fontSize={16} sx={{ mb: 4, pr: 0.5 }}>
                          <p>
                            Did you know you can order Wine Smash to play at home or as a gift? Please click this link to order.
                          </p>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>}

              </Grid>

              <a
                href={isDefaultVenue ? wineryData?.link || 'https://winesmash.com/shop' : 'https://winesmash.com/'}
                target="_blank"
                rel="noreferrer"
              >
                <MuiButton type="submit">
                  <Typography fontFamily="DMSansMedium" fontSize={16} sx={{ color: 'white' }}>
                    Buy
                  </Typography>
                </MuiButton>
              </a>
            </Box>
            <EmailInput
              sendEmail={sendEmail}
              loading={loading}
              emailSent={emailSent}
              score={emailTemplateProps.score}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', fontSize: '3rem', justifyContent: 'space-evenly' }}>
          { wineryData?.instagramUrl &&
            <a href={wineryData.instagramUrl} target="_blank" rel="noreferrer" style={{ color: '#2e2a26' }}>
              <FontAwesomeIcon icon={faInstagram} />
            </a>}
          { wineryData?.tikTokUrl &&
            <a href={wineryData.tikTokUrl} target="_blank" rel="noreferrer" style={{ color: '#2e2a26' }}>
              <FontAwesomeIcon icon={faTiktok} />
            </a> }
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button sx={{ mb: 2 }} onClick={playAgain}>
            <Typography fontFamily="DMSansBold" fontSize={18}>
             { isMultiplayer ? 'Start New Game' : 'Play again' }
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default InvestorDeck
